import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  CheckCircle2,
  PackageSearch,
  UserRound,
} from "lucide-react";

export default function AdminUnmatched() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(null);
  const [createTarget, setCreateTarget] = useState(null);
  const [createForm, setCreateForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    password: "",
  });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [unmatched, users] = await Promise.all([
        axios.get("/api/admin/shipments/items/unmatched"),
        axios.get("/api/admin/users"),
      ]);
      setItems(unmatched.data);
      setCustomers(users.data);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Could not load unmatched shipments",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const match = async (item, userId) => {
    if (!userId) return;
    setMatching(item.id);
    try {
      const response = await axios.put(
        `/api/admin/shipments/items/${item.id}/match`,
        { user_id: Number(userId) },
      );
      setItems((previous) => previous.filter((entry) => entry.id !== item.id));
      toast.success(`${response.data.matchedItems || 1} shipment(s) matched`);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to match shipment");
    } finally {
      setMatching(null);
    }
  };

  const createCustomer = async (event) => {
    event.preventDefault();
    if (!createTarget) return;
    setCreating(true);
    try {
      await axios.post("/api/admin/users", {
        ...createForm,
        shipping_mark: createTarget.shipping_mark,
      });
      setItems((previous) =>
        previous.filter((entry) => entry.id !== createTarget.id),
      );
      setCreateTarget(null);
      setCreateForm({ full_name: "", email: "", phone: "", password: "" });
      toast.success("Customer created and shipment assigned");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create customer");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="admin-match-page">
      <div className="page-header admin-match-header">
        <Link to="/admin" className="back-btn" aria-label="Back to admin">
          <ArrowLeft size={18} />
        </Link>
        <div>
          <p className="admin-match-eyebrow">IMPORT REVIEW</p>
          <h1>Unmatched shipments</h1>
          <p>
            Assign imported shipments whose shipping mark does not belong to a
            registered customer yet.
          </p>
        </div>
      </div>
      <div className="page-content admin-match-content">
        <div className="admin-match-summary">
          <div className="admin-match-summary-icon">
            <PackageSearch size={21} />
          </div>
          <div>
            <strong>
              {items.length} shipment{items.length === 1 ? "" : "s"} waiting for
              a customer
            </strong>
            <p>
              Match an existing customer, or create one using the shipping mark
              from the sheet.
            </p>
          </div>
          <Link to="/admin/users" className="btn btn-outline btn-sm">
            Create customer
          </Link>
        </div>
        {loading ? (
          <div className="loading-center">
            <div className="spinner spinner-dark" />
          </div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <div className="icon">
              <CheckCircle2 size={34} />
            </div>
            <p style={{ fontWeight: 700 }}>Everything is matched</p>
            <p>New shipments without a registered customer will appear here.</p>
          </div>
        ) : (
          <div className="admin-match-list">
            {items.map((item) => (
              <div className="admin-match-card" key={item.id}>
                <div className="admin-match-card-icon">
                  <PackageSearch size={19} />
                </div>
                <div className="admin-match-card-info">
                  <p className="admin-match-card-name">
                    {item.goods_name || "Shipment item"}
                  </p>
                  <p className="admin-match-card-ref">
                    {item.shipping_mark}{" "}
                    {item.tracking_number ? `· ${item.tracking_number}` : ""}
                  </p>
                  <p className="admin-match-card-meta">
                    {item.filename || `Batch #${item.upload_id}`} · GHS{" "}
                    {Number(item.final_fee || 0).toFixed(2)}
                  </p>
                </div>
                <div className="admin-match-action">
                  <UserRound size={15} />
                  <select
                    aria-label={`Assign ${item.shipping_mark}`}
                    value=""
                    onChange={(event) => match(item, event.target.value)}
                    disabled={matching === item.id}
                  >
                    <option value="">
                      {matching === item.id ? "Matching..." : "Match customer"}
                    </option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.full_name} · {customer.shipping_mark}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => setCreateTarget(item)}
                  >
                    Create account
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {createTarget && (
          <div className="admin-match-modal-backdrop">
            <form className="admin-match-modal" onSubmit={createCustomer}>
              <p className="admin-match-eyebrow">CREATE FROM SHIPMENT</p>
              <h2>New customer for {createTarget.shipping_mark}</h2>
              <p className="admin-match-modal-copy">
                The shipment will be assigned automatically to this customer.
              </p>
              {["full_name", "email", "phone", "password"].map((field) => (
                <input
                  key={field}
                  className="input-field"
                  required
                  placeholder={field.replace("_", " ")}
                  type={
                    field === "password"
                      ? "password"
                      : field === "email"
                        ? "email"
                        : "text"
                  }
                  value={createForm[field]}
                  onChange={(event) =>
                    setCreateForm((previous) => ({
                      ...previous,
                      [field]: event.target.value,
                    }))
                  }
                />
              ))}
              <div className="admin-match-modal-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setCreateTarget(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={creating}
                >
                  {creating ? "Creating..." : "Create and assign"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
